import {
  Box,
  Typography,
} from '@mui/material';
import { T, Icon, stitchPill } from './stitch';
import { formatDate } from './projectMeta';


// Draaidag Card
// Losse punten uit een einde-draaidag-rapport die nog actie vragen. Alleen tonen
// wat aan staat: een rij met acht keer "nee" leest als ruis, terwijl juist die
// ene "materiaal moet nog terug" moet opvallen.
const REPORT_ACTIES = [
  { key: 'beeldSyncen', label: 'Beeld nog syncen', icon: 'sync' },
  { key: 'propsOpruimen', label: 'Props nog opruimen', icon: 'inventory_2' },
  { key: 'materiaalTerugbrengen', label: 'Materiaal nog terug', icon: 'undo' },
  { key: 'materiaalSchade', label: 'Materiaalschade', icon: 'warning' },
  { key: 'transcriptieNodig', label: 'Transcriptie nodig', icon: 'subtitles' },
  { key: 'stockbeeldmateriaal', label: 'Stockbeeld geschoten', icon: 'photo_library' },
];


/**
 * Eén einde-draaidag-inzending: de briefing voor de editor plus wat er na de
 * draaidag nog open staat. Webformulier-inzendingen komen via de Airtable-sync
 * binnen; de FF Crew-app schrijft sinds aug 2026 rechtstreeks in Postgres.
 */
function EindeDraaidagRapport({ rapport, toonDraaidag = false }) {
  const opnames = rapport.opnames || [];
  const fotos = rapport.fotos || [];
  const acties = REPORT_ACTIES.filter(a => rapport[a.key]);
  const opmerkingen = [
    { label: 'Voor de projectmanager', tekst: rapport.opmerkingPM },
    { label: 'Materiaalschade', tekst: rapport.opmerkingSchade },
    { label: 'Stockmateriaal', tekst: rapport.opmerkingStock },
    { label: 'Audio', tekst: rapport.opmerkingAudio },
  ].filter(o => o.tekst);

  return (
    <Box sx={{ p: 2, borderRadius: 2, bgcolor: T.surfaceLow, border: `1px solid ${T.outlineVariant}` }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
        <Icon name="assignment_turned_in" size={16} sx={{ color: T.emerald }} />
        <Typography variant="caption" sx={{ fontFamily: T.fontBody, fontWeight: 700, color: T.secondary }}>
          Einde draaidag
        </Typography>
        <Typography variant="caption" sx={{ fontFamily: T.fontBody, color: T.outline }}>
          {[toonDraaidag ? rapport.draaidagNaam : null, rapport.ingevuldDoor, formatDate(rapport.ingevuldOp)]
            .filter(Boolean)
            .join(' · ')}
        </Typography>
      </Box>

      {rapport.briefing && (
        <Typography
          variant="body2"
          sx={{ fontFamily: T.fontBody, whiteSpace: 'pre-wrap', color: T.secondary, mb: opnames.length || acties.length || opmerkingen.length ? 1.5 : 0 }}
        >
          {rapport.briefing}
        </Typography>
      )}

      {opnames.map((url, idx) => (
        // De SAS-URL is enkele uren geldig; bij een lange sessie kan hij
        // verlopen — een refresh van de pagina levert een nieuwe.
        <Box key={idx} component="audio" controls preload="none" src={url} sx={{ width: '100%', mb: 1 }} />
      ))}

      {fotos.length > 0 && (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
          {fotos.map((url, idx) => (
            <Box
              key={idx}
              component="a"
              href={url}
              target="_blank"
              rel="noreferrer"
              sx={{ display: 'block', width: 96, height: 96, borderRadius: 1.5, overflow: 'hidden', border: `1px solid ${T.outlineVariant}` }}
            >
              <Box component="img" src={url} alt={`Foto ${idx + 1}`} loading="lazy" sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
            </Box>
          ))}
        </Box>
      )}

      {acties.length > 0 && (
        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 0.5 }}>
          {acties.map(a => (
            <Typography key={a.key} component="span" sx={{ ...stitchPill('#92400e', T.amberBg), display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
              <Icon name={a.icon} size={12} />
              {a.label}
            </Typography>
          ))}
        </Box>
      )}

      {(rapport.quitclaimStatus || (rapport.audiokanalen || []).length > 0) && (
        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 0.75 }}>
          {rapport.quitclaimStatus && (
            <Typography component="span" sx={stitchPill(T.secondary, T.surfaceHigh)}>
              {'\u{1F4CB}'} {rapport.quitclaimStatus}
            </Typography>
          )}
          {(rapport.audiokanalen || []).map(kanaal => (
            <Typography key={kanaal} component="span" sx={stitchPill(T.secondary, T.surfaceHigh)}>
              {'\u{1F3A7}'} {kanaal}
            </Typography>
          ))}
        </Box>
      )}

      {opmerkingen.map(o => (
        <Box key={o.label} sx={{ mt: 1.25 }}>
          <Typography variant="caption" sx={{ fontFamily: T.fontBody, color: T.outline }} display="block">
            {o.label}
          </Typography>
          <Typography variant="body2" sx={{ fontFamily: T.fontBody, whiteSpace: 'pre-wrap', color: T.secondary }}>
            {o.tekst}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

export default EindeDraaidagRapport;
